import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import UserAvatar from '@/components/UI/UserAvatar';
import type { APITableAsyncExportCallback, AsyncExportRoute } from '@/model/AsyncExport.model';
import type { ID } from '@/model/Common.model';
import type { State } from '@/model/State.model';
import type { StudyApiId } from '@/model/Study.model';
import type { StudyUser } from '@/model/User.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useAsyncExport } from '@/support/Hooks/api/useAsyncExport';
import { web as webRoute } from '@/support/route';
import { RouteService } from '@/support/RouteService';
import { featuresSelector } from '@/support/Selectors';
import { createSelector } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

const featureSelector = createSelector([featuresSelector], (features) => ({
  asyncExportsEnabled: features.async_exports === true,
}));

interface RandomizationReport {
  created_at: string;
  user: StudyUser;
  id: string;
  animals_selected: number;
  animals_randomized: number;
}

const buildColumns = (studyId: ID): Array<TanStackColumnDef<RandomizationReport>> => [
  {
    id: 'created_at',
    header: 'Date of Randomization',
    accessorKey: 'created_at',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: RandomizationReport } }) => (
      <Link
        to={webRoute('randomization.report', {
          id: studyId,
          reportApiId: original.id,
        })}
        className="link blue"
      >
        <DateTimeRenderer value={original.created_at} defaultResponse="-" />
      </Link>
    ),
  },
  {
    id: 'user',
    header: 'User',
    accessorKey: 'user',
    cell: ({ row: { original } }: { row: { original: RandomizationReport } }) => (
      <UserAvatar user={original?.user} tooltip={original?.user?.name ?? original?.user?.email} />
    ),
    isVisible: true,
    enableSorting: false,
  },
  {
    id: 'preexclusion',
    header: 'Animals Selected',
    accessorKey: 'animals_selected',
    isVisible: true,
    enableSorting: false,
  },
  {
    id: 'postexclusion',
    header: 'Animals Randomized',
    accessorKey: 'animals_randomized',
    isVisible: true,
    enableSorting: false,
  },
];

interface ReportsProps {
  studyApiId: StudyApiId;
  studyId: ID;
}

const Reports: React.FC<ReportsProps> = ({ studyApiId, studyId }) => {
  const settings = useSelector((state: State) => state.ui.settings);
  const { asyncExportsEnabled } = useSelector(featureSelector);
  const columns = buildColumns(studyId);

  const triggerAsyncExport = useAsyncExport();

  const { invoke: createRandomizationExport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/exports/randomization',
    options: { invokeOnInit: false, onError: { toast: false, throw: false, slug: 'randomization-async-export' } },
  });

  const exportRoutes = useMemo(() => {
    if (asyncExportsEnabled) {
      const asyncRoutes: Array<AsyncExportRoute> = [
        {
          endpoint: 'POST /api/v1/studies/{studyApiId}/exports/randomization',
          params: { studyApiId },
          label: 'Export study randomization',
          noFilters: true,
        },
      ];
      return asyncRoutes;
    }
    return [
      {
        label: 'Export study randomization',
        url: 'studies.randomization.export',
        params: { studyApiId },
        noFilters: true,
      },
    ];
  }, [studyApiId, asyncExportsEnabled]);

  const handleAsyncExport = useCallback<APITableAsyncExportCallback>(
    async ({ route }) => {
      await triggerAsyncExport(() => createRandomizationExport({ path: route.params }));
    },
    [createRandomizationExport, triggerAsyncExport]
  );

  return (
    <>
      <APITable
        apiInfo={{
          type: 'internalApi',
          route: RouteService.api({
            endpoint: 'GET /api/v1/studies/{studyId}/randomizations',
            path: { studyId: studyApiId.toString() },
          }).url,
        }}
        columns={columns}
        hideSearch={true}
        hideFilter={true}
        testIdPrefix="reports"
        exportRoutes={exportRoutes}
        onAsyncExport={asyncExportsEnabled ? handleAsyncExport : undefined}
        reduxTableName={'randomization'}
        AsideComponent={
          <div className="flex w-100 justify-start mt2">
            <p data-testid="reports-animals-desc">
              Click the Date of Randomization to view report. To randomize animals into groups use Bulk actions in{' '}
              {settings?.studyState?.pageStates?.animals ? (
                'Animals'
              ) : (
                <Link
                  data-testid="reports-animals-link"
                  className="link blue"
                  to={webRoute('studies.animals', { id: studyId })}
                >
                  Animals
                </Link>
              )}
            </p>
          </div>
        }
      />
    </>
  );
};
export default Reports;
